import { useMemo, useState } from 'react';
import {
  HashRouter,
  Route,
  RouteComponentProps,
  Switch,
} from 'react-router-dom';

import Close from './Close';
import DigitalCheckIn from './DigitalCheckIn';
import Directory from './Directory';
import Intent from './Intent';
import Login from './Login';
import EhrTimeSlotBookingRouter from './EhrTimeSlotBooking/Router';
import MessageRequest from './Login/MessageRequest';
import PatientTermsOfUse from './Schedule/Form/PatientTermsOfUse';
import PreAuthLogin from './Login/PreAuthLogin';
import ReasonForVisitSelector from './Schedule/Form/ReasonForVisitSelector';
import ReasonForVisitReturnVisitPage from './Schedule/Form/ReasonForVisitSelector/ReturnVisit';
import ReceiptAuth from './Login/ReceiptAuth';
import ReceiptPage from './Payments/ReceiptPage';
import Registration from './Login/Registration';
import RegistrationRequest from './Login/Registration/Request';
import Schedule from './Schedule';
import Unsubscribe from './Unsubscribe';

import styles from './App.module.css';

import { alpha, lighten } from '../utils/color';
import { getCustomer } from '../utils/practice';

import { Notifications } from './Notifications';
import { FormRouter } from './formRouter';
import { ConfirmAppointmentScene } from './ConfirmAppointment';
import { AppointmentLogin } from './ConfirmAppointment/AppointmentLogin';
import ErrorBoundary from '../components/ErrorBoundary';
import BackButtonContext from './BackButtonContext';
import { DocumentHeader } from './DocumentHeader';
import { Captcha } from './Captcha';
import LanguageWrapper from '../LanguageWrapper';

export interface PatientRouteParams {
  authenticationCode: string;
  formSetId?: string;
  formId?: string;
  reviewFormId?: string;
}

export interface PreAuthLoginRouteParams {
  authenticationCode: string;
  authBypassCode: string;
}

export type PatientRouteProps = RouteComponentProps<PatientRouteParams>;

const routes: {
  [name: string]: {
    path: string;
    component: React.ComponentType<any>;
    exact?: boolean;
  };
} = {
  loginAppointment: {
    path: '/confirm/login/:authenticationCode',
    component: AppointmentLogin,
  },
  confirmAppointment: {
    path: '/confirm/:authenticationCode',
    component: ConfirmAppointmentScene,
  },
  directory: {
    path: '/directory/:organizationSlug',
    component: Directory,
  },
  messageRequestLink: {
    path: '/message-request/:requestCode/:requestId?',
    component: MessageRequest,
  },
  requestLink: {
    path: '/request',
    component: RegistrationRequest,
  },
  registration: {
    path: '/register/:registrationCode',
    component: Registration,
  },
  schedule: {
    path: '/schedule/:schedulingCode',
    component: Schedule,
  },
  close: {
    path: '/close',
    component: Close,
  },
  inbound: {
    path: '/get-started/:schedulingCode',
    component: () => <Schedule updateDocumentTitle={false} />,
  },
  ehrTimeSlotBooking: {
    path: '/ehr-time-slot-bookings',
    component: EhrTimeSlotBookingRouter,
  },
  preAuthLogin: {
    path: '/pre-auth-login/:authenticationCode/:authBypassCode',
    component: PreAuthLogin,
  },
  login: { path: '/login/:authenticationCode', component: Login },
  form: { path: '/form', component: FormRouter },
  receiptLogin: {
    path: '/receipt/:authenticationCode/login',
    component: ReceiptAuth,
  },
  receiptDisplay: {
    path: '/receipt/:authenticationCode',
    component: ReceiptPage,
  },
  reasonForVisit: {
    path: '/reasonForVisitUserTesting',
    component: ReasonForVisitSelector,
  },
  ReasonForVisitReturnVisit: {
    path: '/reasonForVisitUserTestingEnd',
    component: ReasonForVisitReturnVisitPage,
  },
  patientTermsOfUse: {
    path: '/patient-terms-of-use',
    component: PatientTermsOfUse,
  },
  unsubscribe: {
    path: '/unsubscribe/:unsubscribeCode',
    component: Unsubscribe,
  },
  intent: {
    path: '/intents/:intentId',
    component: Intent,
  },
  digitalCheckIn: {
    path: '/encounter/:encounterId/digital-check-in/:authCode',
    component: DigitalCheckIn,
  },
};

const AppRoutes: React.FC = () => {
  const components = Object.entries(routes).map(([key, route]) => (
    <Route
      key={key}
      exact={route.exact}
      path={route.path}
      component={route.component}
    />
  ));
  return (
    <div className={styles.App}>
      <Switch>{components}</Switch>
    </div>
  );
};

export const Router: React.FC = () => {
  const [isBackButtonShown, setIsBackButtonShown] = useState(true);
  const backButtonContextValue = useMemo(
    () => ({ isBackButtonShown, setIsBackButtonShown }),
    [isBackButtonShown],
  );

  const customer = getCustomer();
  if (customer.primaryHexColor) {
    document.documentElement.style.setProperty(
      '--color-primary',
      customer.primaryHexColor,
    );

    document.documentElement.style.setProperty(
      '--color-primary-dark',
      lighten(customer.primaryHexColor, -10),
    );

    document.documentElement.style.setProperty(
      '--color-primary-darker',
      lighten(customer.primaryHexColor, -20),
    );

    document.documentElement.style.setProperty(
      '--color-primary-transparent',
      alpha(customer.primaryHexColor, 0.1),
    );

    document.documentElement.style.setProperty(
      '--color-primary-transparent-darker',
      alpha(customer.primaryHexColor, 0.2),
    );
  }
  if (customer.secondaryHexColor) {
    document.documentElement.style.setProperty(
      '--color-primary',
      customer.secondaryHexColor,
    );
  }
  return (
    <BackButtonContext.Provider value={backButtonContextValue}>
      <HashRouter>
        <LanguageWrapper>
          <ErrorBoundary>
            <DocumentHeader />
            <Captcha />
            <AppRoutes />
            <Notifications />
          </ErrorBoundary>
        </LanguageWrapper>
      </HashRouter>
    </BackButtonContext.Provider>
  );
};
